import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Settings2, Save, Users, Mail, Eye, Tag, Share2, Copy, CheckCheck, Image as ImageIcon, X } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";

const TEAL = "#0D9488";

/** Verified coaches use this card (on MyClients) to edit their public
 *  directory profile, accepting-clients status, weekly digest opt-in, and
 *  the visibility of each section on their shareable public page.
 */
export default function CoachProfileCard({ onProfileLoad }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowed, setAllowed] = useState([]);
  const [publicUrl, setPublicUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [hero, setHero] = useState(null);
  const [heroBusy, setHeroBusy] = useState(false);
  const [form, setForm] = useState({
    coach_bio: "",
    coach_specialties: [],
    accepting_clients: true,
    directory_enabled: false,
    coach_digest_enabled: false,
    public_show_bio: true,
    public_show_specialties: true,
    public_show_reviews: true,
    public_show_plans: true,
    public_show_contact: true,
  });

  useEffect(() => {
    api.get("/coach/profile")
      .then(({ data }) => {
        setForm({
          coach_bio: data.coach_bio || "",
          coach_specialties: data.coach_specialties || [],
          accepting_clients: !!data.accepting_clients,
          directory_enabled: !!data.directory_enabled,
          coach_digest_enabled: !!data.coach_digest_enabled,
          public_show_bio: !!data.public_show_bio,
          public_show_specialties: !!data.public_show_specialties,
          public_show_reviews: !!data.public_show_reviews,
          public_show_plans: !!data.public_show_plans,
          public_show_contact: !!data.public_show_contact,
        });
        setAllowed(data.allowed_specialties || []);
        if (data.handle) setPublicUrl(`${window.location.origin}/coach/@${data.handle}`);
        onProfileLoad?.(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // Load hero image separately (small doc)
    api.get("/coach/hero").then(({ data }) => setHero(data.hero_data_url || null)).catch(() => {});
  }, [onProfileLoad]);

  const uploadHero = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    if (file.size > 2_000_000) { toast.error("Image too big (2MB max)."); return; }
    setHeroBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.patch("/coach/hero", { hero_data_url: dataUrl });
      setHero(dataUrl);
      toast.success("Hero image uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHeroBusy(false); }
  };

  const clearHero = async () => {
    setHeroBusy(true);
    try {
      await api.patch("/coach/hero", { hero_data_url: null });
      setHero(null);
      toast.success("Hero image removed");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHeroBusy(false); }
  };

  const patch = async (partial) => {
    const next = { ...form, ...partial };
    setForm(next);
    try {
      await api.patch("/coach/profile", partial);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleSpecialty = (s) => {
    const has = form.coach_specialties.includes(s);
    let next;
    if (has) next = form.coach_specialties.filter((x) => x !== s);
    else if (form.coach_specialties.length < 5) next = [...form.coach_specialties, s];
    else { toast.error("Pick at most 5 specialties."); return; }
    setForm((f) => ({ ...f, coach_specialties: next }));
    api.patch("/coach/profile", { coach_specialties: next }).catch((err) => toast.error(formatApiError(err)));
  };

  const saveBio = async () => {
    setSaving(true);
    try {
      await api.patch("/coach/profile", { coach_bio: form.coach_bio });
      toast.success("Coach bio saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bg-white border border-slate-200 rounded-xl p-6 h-52 animate-pulse" data-testid="coach-profile-loading" />;
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="coach-profile-card">
      <div className="flex items-center gap-2 mb-1">
        <Settings2 className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>Coach settings</div>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-1">Your coach profile</h3>
      <p className="text-sm text-slate-500 mb-4">Control how you appear in the Find-a-Coach directory and how often you get client updates.</p>

      <div className="space-y-4">
        <ToggleRow
          icon={Eye}
          label="Show me in the Find-a-Coach directory"
          hint="Verified coaches with this on appear on every user's dashboard."
          checked={form.directory_enabled}
          onCheckedChange={(v) => patch({ directory_enabled: v })}
          testid="coach-toggle-directory"
        />
        <ToggleRow
          icon={Users}
          label="I'm accepting new clients"
          hint="When off, users still see your profile but can't request access."
          checked={form.accepting_clients}
          onCheckedChange={(v) => patch({ accepting_clients: v })}
          testid="coach-toggle-accepting"
        />
        <ToggleRow
          icon={Mail}
          label="Email me a weekly client digest"
          hint="Every Monday 08:00 UTC. One summary email covering all your active clients."
          checked={form.coach_digest_enabled}
          onCheckedChange={(v) => patch({ coach_digest_enabled: v })}
          testid="coach-toggle-digest"
        />

        {/* Public page visibility */}
        {publicUrl && (
          <div className="pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-2">
              <Share2 className="w-3.5 h-3.5" style={{ color: TEAL }} />
              <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Public page</span>
            </div>

            {/* Hero image */}
            <div className="mb-3">
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 flex items-center gap-1.5">
                <ImageIcon className="w-3 h-3" /> Cover image
              </div>
              {hero ? (
                <div className="relative rounded-lg overflow-hidden border border-slate-200 mb-2" data-testid="coach-hero-preview">
                  <img src={hero} alt="Coach hero" className="w-full h-32 object-cover" />
                  <button
                    type="button"
                    onClick={clearHero}
                    disabled={heroBusy}
                    className="absolute top-2 right-2 w-7 h-7 bg-white/90 hover:bg-white rounded-md flex items-center justify-center text-rose-600 shadow"
                    data-testid="coach-hero-remove"
                    aria-label="Remove cover image"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="coach-hero-file"
                  className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-lg py-6 cursor-pointer hover:border-teal-300 hover:bg-teal-50/40 transition-colors mb-2"
                  data-testid="coach-hero-dropzone"
                >
                  <ImageIcon className="w-6 h-6 text-slate-400 mb-1" />
                  <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                    {heroBusy ? "Uploading…" : "Click to upload cover image"}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">PNG or JPG · max 2MB</div>
                </label>
              )}
              <input
                id="coach-hero-file"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => uploadHero(e.target.files?.[0])}
                data-testid="coach-hero-input"
              />
            </div>

            <div className="flex items-center gap-2 bg-teal-50 border border-teal-100 rounded-md px-3 py-2 mb-3" data-testid="coach-public-url">
              <div className="text-[12px] font-mono-num text-teal-800 truncate flex-1">{publicUrl}</div>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(publicUrl).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                    toast.success("Public link copied");
                  });
                }}
                className="text-[10px] uppercase tracking-widest font-semibold text-teal-700 hover:text-teal-900 flex items-center gap-1 shrink-0"
                data-testid="coach-public-copy"
              >
                {copied ? <><CheckCheck className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] uppercase tracking-widest font-semibold text-teal-700 hover:text-teal-900 flex items-center gap-1 shrink-0"
                data-testid="coach-public-open"
              >
                Open
              </a>
            </div>
            <div className="text-[11px] text-slate-500 leading-relaxed mb-2">
              Show or hide each section on your public page. The page is only live while the directory listing is on.
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <VisRow label="Bio" checked={form.public_show_bio} onCheckedChange={(v) => patch({ public_show_bio: v })} testid="vis-bio" />
              <VisRow label="Specialties" checked={form.public_show_specialties} onCheckedChange={(v) => patch({ public_show_specialties: v })} testid="vis-specialties" />
              <VisRow label="Reviews" checked={form.public_show_reviews} onCheckedChange={(v) => patch({ public_show_reviews: v })} testid="vis-reviews" />
              <VisRow label="Plans" checked={form.public_show_plans} onCheckedChange={(v) => patch({ public_show_plans: v })} testid="vis-plans" />
              <VisRow label="Contact" checked={form.public_show_contact} onCheckedChange={(v) => patch({ public_show_contact: v })} testid="vis-contact" />
            </div>
          </div>
        )}

        <div>
          <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 flex items-center gap-1.5">
            <Tag className="w-3 h-3" /> Your specialties (pick up to 5)
          </label>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="coach-specialty-chips">
            {allowed.map((s) => {
              const active = form.coach_specialties.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSpecialty(s)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-mono-num uppercase tracking-wider border transition-colors ${
                    active
                      ? "bg-[#0D9488] text-white border-[#0D9488]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-teal-300 hover:text-teal-700"
                  }`}
                  data-testid={`coach-specialty-${s}`}
                >
                  {s.replace("-", " ")}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">
            Public bio (shown in the directory)
          </label>
          <Textarea
            value={form.coach_bio}
            onChange={(e) => setForm((f) => ({ ...f, coach_bio: e.target.value.slice(0, 600) }))}
            placeholder="e.g. Certified nutrition coach — 8 yrs helping athletes dial in peptide + macro protocols."
            rows={3}
            className="bg-white border-slate-200 text-slate-900 focus-visible:ring-0 focus-visible:border-[#0D9488]"
            data-testid="coach-bio-input"
          />
          <div className="flex items-center justify-between mt-1.5">
            <div className="text-[10px] text-slate-400 font-mono-num">{form.coach_bio.length}/600</div>
            <Button
              type="button"
              onClick={saveBio}
              disabled={saving}
              size="sm"
              className="text-white"
              style={{ background: TEAL }}
              data-testid="coach-bio-save"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save bio"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ icon: Icon, label, hint, checked, onCheckedChange, testid }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-b-0">
      <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: TEAL }} />
      <div className="flex-1">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{hint}</div>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="shrink-0 data-[state=checked]:bg-[#0D9488]"
        data-testid={testid}
      />
    </div>
  );
}

function VisRow({ label, checked, onCheckedChange, testid }) {
  return (
    <label className="flex items-center gap-2 py-1 cursor-pointer">
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="data-[state=checked]:bg-[#0D9488]"
        data-testid={`coach-${testid}`}
      />
      <span className="text-sm text-slate-800">{label}</span>
    </label>
  );
}
